import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  DeleteOutlineRounded,
  ArrowUpwardRounded,
  ArrowDownwardRounded,
} from '@mui/icons-material';
import { useTenantStore } from '../../../stores/tenant.store';
import { errorText } from '../../../lib/error-text';
import {
  surveyApi,
  type SurveyQuestionDto,
  type SurveyQuestionType,
  type SurveyTemplateCategory,
  type SurveyTemplateDto,
} from '../../../lib/api/survey.api';

interface Props {
  template?: SurveyTemplateDto; // undefined = create
  onClose: () => void;
}

const QUESTION_TYPES: SurveyQuestionType[] = [
  'likert',
  'nps',
  'stars',
  'single_choice',
  'multi_choice',
  'text',
];

function newQuestionId(): string {
  // Stable per-question key. crypto.randomUUID is available in the browser.
  return `q_${crypto.randomUUID().slice(0, 8)}`;
}

function defaultScale(type: SurveyQuestionType) {
  if (type === 'nps') return { min: 0, max: 10 };
  return { min: 1, max: 5 };
}

export function SurveyTemplateEditorDialog({ template, onClose }: Props) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const queryClient = useQueryClient();
  const isEdit = !!template;

  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [category, setCategory] = useState<SurveyTemplateCategory>(
    template?.category ?? 'end_of_course',
  );
  const [questions, setQuestions] = useState<SurveyQuestionDto[]>(template?.questions ?? []);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = { name, category, questions, description };
      return isEdit
        ? surveyApi.updateTemplate({
            tenantId: currentTenantId!,
            templateId: template!.id,
            ...payload,
          })
        : surveyApi.createTemplate({ tenantId: currentTenantId!, ...payload });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-templates', currentTenantId] });
      onClose();
    },
    onError: (e) => setError(errorText(e, 'error')),
  });

  const addQuestion = () => {
    setQuestions((qs) => [
      ...qs,
      {
        id: newQuestionId(),
        label: '',
        type: 'likert',
        required: true,
        scale: defaultScale('likert'),
      },
    ]);
  };

  const patchQuestion = (i: number, patch: Partial<SurveyQuestionDto>) => {
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  };

  const changeType = (i: number, type: SurveyQuestionType) => {
    const base: SurveyQuestionDto = { ...questions[i]!, type };
    // Reset type-specific fields.
    delete base.options;
    delete base.scale;
    if (type === 'likert' || type === 'nps' || type === 'stars') base.scale = defaultScale(type);
    if (type === 'single_choice' || type === 'multi_choice') base.options = [];
    patchQuestion(i, base);
  };

  const move = (i: number, dir: -1 | 1) => {
    setQuestions((qs) => {
      const next = [...qs];
      const j = i + dir;
      if (j < 0 || j >= next.length) return qs;
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  };

  const removeQuestion = (i: number) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  const canSave = name.trim().length > 0 && questions.length > 0 && !save.isPending;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? t('surveys.editor.editTitle') : t('surveys.editor.newTitle')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label={t('surveys.editor.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label={t('surveys.editor.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="tpl-category">{t('surveys.editor.category')}</InputLabel>
            <Select
              labelId="tpl-category"
              label={t('surveys.editor.category')}
              value={category}
              onChange={(e) => setCategory(e.target.value as SurveyTemplateCategory)}
            >
              <MenuItem value="end_of_course">{t('surveys.editor.catEndOfCourse')}</MenuItem>
              <MenuItem value="periodic">{t('surveys.editor.catPeriodic')}</MenuItem>
              <MenuItem value="generic">{t('surveys.editor.catGeneric')}</MenuItem>
            </Select>
          </FormControl>

          <Divider textAlign="left">
            <Typography variant="overline">{t('surveys.editor.questions')}</Typography>
          </Divider>

          {questions.map((q, i) => (
            <QuestionEditor
              key={q.id}
              q={q}
              index={i}
              total={questions.length}
              onChange={(patch) => patchQuestion(i, patch)}
              onChangeType={(type) => changeType(i, type)}
              onMove={(dir) => move(i, dir)}
              onRemove={() => removeQuestion(i)}
            />
          ))}

          <Button startIcon={<AddRounded />} onClick={addQuestion} variant="outlined">
            {t('surveys.editor.addQuestion')}
          </Button>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('surveys.editor.cancel')}</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => save.mutate()}>
          {save.isPending ? t('surveys.editor.saving') : t('surveys.editor.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function QuestionEditor({
  q,
  index,
  total,
  onChange,
  onChangeType,
  onMove,
  onRemove,
}: {
  q: SurveyQuestionDto;
  index: number;
  total: number;
  onChange: (patch: Partial<SurveyQuestionDto>) => void;
  onChangeType: (type: SurveyQuestionType) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('sis');
  const isScale = q.type === 'likert' || q.type === 'nps' || q.type === 'stars';
  const isChoice = q.type === 'single_choice' || q.type === 'multi_choice';

  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          #{index + 1}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" disabled={index === 0} onClick={() => onMove(-1)}>
          <ArrowUpwardRounded fontSize="small" />
        </IconButton>
        <IconButton size="small" disabled={index === total - 1} onClick={() => onMove(1)}>
          <ArrowDownwardRounded fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={onRemove}>
          <DeleteOutlineRounded fontSize="small" />
        </IconButton>
      </Stack>

      <Stack spacing={2}>
        <TextField
          label={t('surveys.editor.questionLabel')}
          value={q.label}
          onChange={(e) => onChange({ label: e.target.value })}
          fullWidth
          size="small"
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id={`type-${q.id}`}>{t('surveys.editor.type')}</InputLabel>
            <Select
              labelId={`type-${q.id}`}
              label={t('surveys.editor.type')}
              value={q.type}
              onChange={(e) => onChangeType(e.target.value as SurveyQuestionType)}
            >
              {QUESTION_TYPES.map((tp) => (
                <MenuItem key={tp} value={tp}>
                  {t(`surveys.editor.types.${tp}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={q.required}
                onChange={(e) => onChange({ required: e.target.checked })}
              />
            }
            label={t('surveys.editor.required')}
          />
        </Stack>

        {isScale && q.scale && (
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              type="number"
              size="small"
              label={t('surveys.editor.scaleMin')}
              value={q.scale.min}
              onChange={(e) => onChange({ scale: { ...q.scale!, min: Number(e.target.value) } })}
              sx={{ width: 90 }}
            />
            <TextField
              type="number"
              size="small"
              label={t('surveys.editor.scaleMax')}
              value={q.scale.max}
              onChange={(e) => onChange({ scale: { ...q.scale!, max: Number(e.target.value) } })}
              sx={{ width: 90 }}
            />
            <TextField
              size="small"
              label={t('surveys.editor.minLabel')}
              value={q.scale.minLabel ?? ''}
              onChange={(e) => onChange({ scale: { ...q.scale!, minLabel: e.target.value } })}
            />
            <TextField
              size="small"
              label={t('surveys.editor.maxLabel')}
              value={q.scale.maxLabel ?? ''}
              onChange={(e) => onChange({ scale: { ...q.scale!, maxLabel: e.target.value } })}
            />
          </Stack>
        )}

        {isChoice && (
          <TextField
            size="small"
            label={t('surveys.editor.options')}
            helperText={t('surveys.editor.optionsHelp')}
            value={(q.options ?? []).join('\n')}
            onChange={(e) =>
              onChange({
                options: e.target.value
                  .split('\n')
                  .map((o) => o.trim())
                  .filter(Boolean),
              })
            }
            multiline
            minRows={2}
            fullWidth
          />
        )}
      </Stack>
    </Box>
  );
}
